import { useContext, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { useAuth } from "../../hooks/useAuth";
import { useDialogFocus } from "../../hooks/useDialogFocus";
import { availableGuidedTours, GUIDED_TOURS, type AvailableGuidedTour } from "./guidedTours";
import { useActiveRosterBoardKind } from "./activeRosterBoard";
import { TourContext } from "./tourContext";
import "./GuidedToursModal.css";

interface GuidedToursModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const noTours = () => false;

/**
 * The Guided tours list, opened from the sidebar. Choosing a page closes the list, goes to the page
 * and replays its tour there once it has loaded. Replays record nothing, so this never changes what
 * auto-starts later.
 */
export const GuidedToursModal = ({ isOpen, onClose }: GuidedToursModalProps) => {
  const context = useContext(TourContext);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const rosterBoardKind = useActiveRosterBoardKind();

  useDialogFocus({ isOpen, dialogRef, onClose, lockScroll: true });

  if (!isOpen) return null;

  // Until the config has loaded, an empty list would claim there are no tours when there may be.
  const loading = context?.status === "idle" || context?.status === "loading";
  const tours = availableGuidedTours(
    location.pathname,
    user?.profile,
    context?.hasPageTour ?? noTours,
    GUIDED_TOURS,
    rosterBoardKind
  );

  const choose = ({ entry, path }: AvailableGuidedTour) => {
    onClose();
    context?.requestReplay(entry.featureKey, entry.keys, path);
    // Already there: keep the page's own search params (a filter, a page of a table).
    if (path !== location.pathname) navigate(path);
  };

  // Portalled: the sidebar that opens it is `position: sticky`, which is a stacking context the
  // overlay's z-index could not escape.
  return createPortal(
    // No backdrop click: Escape and the close button dismiss it, and a click handler on a static
    // element is a finding the accessibility ratchet counts.
    <div className="guided-tours-overlay">
      <div
        ref={dialogRef}
        className="guided-tours"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="guided-tours__header">
          <h2 className="guided-tours__title" id={titleId}>
            Guided tours
          </h2>
          <ModalCloseButton onClick={onClose} />
        </div>
        {loading ? (
          <p className="guided-tours__empty" role="status" aria-live="polite">
            Loading tours...
          </p>
        ) : tours.length === 0 ? (
          <p className="guided-tours__empty">There are no tours to show you right now.</p>
        ) : (
          <>
            <p className="guided-tours__intro">Pick a page to walk through its tour again.</p>
            <ul className="guided-tours__list">
              {tours.map((tour) => (
                <li key={tour.entry.id}>
                  <button
                    type="button"
                    className="guided-tours__option"
                    onClick={() => choose(tour)}
                  >
                    {tour.entry.label}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
